import { useState, type ReactNode } from 'react';
import { errorMessage } from '../../api/client';
import { useWaImportContacts } from '../../api/whatsapp';
import type { ConsentCategory, ConsentMethod, ImportResult, WaScope } from '../../api/types';
import { Alert, Button, SelectField, TextField } from '../ui';

/** The consent language shown to people; stored with every contact it applies to. */
export const DEFAULT_CONSENT_TEXT =
  'By checking, you agree to receive marketing messages on WhatsApp at this number. Reply STOP to opt-out. Msg frequency varies.';

interface ParsedRow {
  phone: string;
  name?: string;
  email?: string;
}

/**
 * CSV in, contacts out. The file is parsed in the browser (phone, name and
 * email columns by header or position); the consent block applies to every
 * row and is never pre-checked: without it the people come in as
 * "no consent yet" and cannot be messaged.
 */
export function ContactImporter({
  scope = {},
  onImported,
}: {
  scope?: WaScope;
  onImported: (result: ImportResult, notice: ReactNode) => void;
}) {
  const [csv, setCsv] = useState('');
  const [defaultCountryCode, setDefaultCountryCode] = useState('1');
  const [granted, setGranted] = useState(false);
  const [source, setSource] = useState('import_csv');
  const [method, setMethod] = useState<ConsentMethod>('checkbox');
  const [textShown, setTextShown] = useState(DEFAULT_CONSENT_TEXT);
  const [categories, setCategories] = useState<ConsentCategory[]>(['marketing', 'utility']);
  const [occurredAt, setOccurredAt] = useState('');
  const [progress, setProgress] = useState<number | null>(null);

  const rows = parseCsv(csv);

  const importRun = useWaImportContacts(scope);

  const onSubmit = () =>
    importRun.mutate(
      {
        rows,
        defaultCountryCode: defaultCountryCode.trim() || undefined,
        consent: {
          granted,
          source: source.trim() || 'import_csv',
          method,
          ...(granted ? { textShown: textShown.trim(), categories } : {}),
          ...(occurredAt ? { occurredAt: new Date(occurredAt).toISOString() } : {}),
        },
        onProgress: setProgress,
      },
      {
        onSettled: () => setProgress(null),
        onSuccess: (totals) => {
          setCsv('');
          onImported(
            totals,
            <>
              Imported {totals.received} row{totals.received === 1 ? '' : 's'}: {totals.created}{' '}
              new, {totals.updated} updated, {totals.subscribed} subscribed, {totals.skipped.length}{' '}
              skipped.
            </>,
          );
        },
      },
    );

  const toggle = (c: ConsentCategory) =>
    setCategories((cur) => (cur.includes(c) ? cur.filter((x) => x !== c) : [...cur, c]));

  return (
    <form
      className="form"
      aria-label="Import contacts"
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit();
      }}
      noValidate
    >
      {importRun.error ? <Alert kind="error">{errorMessage(importRun.error)}</Alert> : null}
      <div className="field">
        <label className="field__label" htmlFor="contactsCsv">
          CSV rows
        </label>
        <textarea
          id="contactsCsv"
          className="field__input field__textarea"
          value={csv}
          placeholder={'phone,name,email\n+14155550100,Ada Lovelace,ada@example.com'}
          onChange={(e) => setCsv(e.target.value)}
        />
        <p className="field__hint">
          Paste from a spreadsheet or{' '}
          <label className="linklike">
            choose a file
            <input
              type="file"
              accept=".csv,text/csv"
              hidden
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (!file) return;
                void file.text().then(setCsv);
              }}
            />
          </label>
          . Columns: phone (required), name, email. {rows.length} row{rows.length === 1 ? '' : 's'}{' '}
          recognised.
        </p>
      </div>
      <div className="form form--grid">
        <TextField
          label="Country code for national numbers"
          name="defaultCountryCode"
          value={defaultCountryCode}
          hint='Applied to numbers without a "+" (e.g. 1 for the US).'
          onChange={(e) => setDefaultCountryCode(e.target.value.replace(/\D/g, ''))}
        />
        <TextField
          label="Where did these people opt in?"
          name="source"
          value={source}
          hint="shopify_checkout, website_popup, import_csv, event_signup…"
          onChange={(e) => setSource(e.target.value)}
        />
        <SelectField
          label="How"
          value={method}
          onChange={(e) => setMethod(e.target.value as ConsentMethod)}
        >
          <option value="checkbox">Unchecked checkbox they ticked</option>
          <option value="form">Form they submitted</option>
          <option value="keyword">Keyword they texted</option>
          <option value="verbal">Verbal or in person</option>
          <option value="import">Imported from another system</option>
        </SelectField>
        <TextField
          label="When (optional)"
          name="occurredAt"
          type="datetime-local"
          value={occurredAt}
          onChange={(e) => setOccurredAt(e.target.value)}
        />
      </div>
      <div className="field">
        <label className="field__label" htmlFor="textShown">
          The exact wording they agreed to
        </label>
        <textarea
          id="textShown"
          className="field__input"
          rows={2}
          value={textShown}
          onChange={(e) => setTextShown(e.target.value)}
        />
        <p className="field__hint">
          Stored with each contact as proof; Meta and TCPA audits ask for it.
        </p>
      </div>
      <div className="form__row">
        <label className="field field--check">
          <input
            type="checkbox"
            checked={categories.includes('marketing')}
            onChange={() => toggle('marketing')}
          />{' '}
          Marketing (offers, inventory)
        </label>
        <label className="field field--check">
          <input
            type="checkbox"
            checked={categories.includes('utility')}
            onChange={() => toggle('utility')}
          />{' '}
          Utility (orders, account)
        </label>
      </div>
      <label className="field field--check">
        <input type="checkbox" checked={granted} onChange={(e) => setGranted(e.target.checked)} />{' '}
        <strong>Every person in this file gave this consent.</strong> Leave unchecked to import
        without consent (they cannot be messaged until they opt in).
      </label>
      {progress !== null ? (
        <div
          className="progress"
          role="progressbar"
          aria-valuenow={progress}
          aria-valuemin={0}
          aria-valuemax={100}
        >
          <div className="progress__bar" style={{ width: `${progress}%` }} />
        </div>
      ) : null}
      <div>
        <Button
          type="submit"
          loading={importRun.isPending}
          disabled={
            rows.length === 0 || (granted && (!textShown.trim() || categories.length === 0))
          }
        >
          Import {rows.length || ''} contact{rows.length === 1 ? '' : 's'}
        </Button>
      </div>
    </form>
  );
}

/** Tiny CSV reader: comma / semicolon / tab separated, optional header, quoted fields. */
export function parseCsv(text: string): ParsedRow[] {
  const lines = text
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter(Boolean);
  if (lines.length === 0) return [];
  const sep = lines[0]!.includes('\t') ? '\t' : lines[0]!.includes(';') ? ';' : ',';
  const split = (line: string): string[] => {
    const out: string[] = [];
    let cur = '';
    let quoted = false;
    for (let i = 0; i < line.length; i += 1) {
      const ch = line[i]!;
      if (ch === '"') {
        if (quoted && line[i + 1] === '"') {
          cur += '"';
          i += 1;
        } else quoted = !quoted;
      } else if (ch === sep && !quoted) {
        out.push(cur.trim());
        cur = '';
      } else cur += ch;
    }
    out.push(cur.trim());
    return out;
  };
  const first = split(lines[0]!).map((h) => h.toLowerCase());
  const hasHeader = first.some((h) => /phone|mobile|number|name|email/.test(h));
  const col = {
    phone: hasHeader ? first.findIndex((h) => /phone|mobile|number|whatsapp/.test(h)) : 0,
    name: hasHeader ? first.findIndex((h) => /name/.test(h)) : 1,
    email: hasHeader ? first.findIndex((h) => /e-?mail/.test(h)) : 2,
  };
  if (col.phone < 0) col.phone = 0;
  const rows: ParsedRow[] = [];
  for (const line of hasHeader ? lines.slice(1) : lines) {
    const cells = split(line);
    const phone = cells[col.phone];
    if (!phone) continue;
    const name = col.name >= 0 ? cells[col.name] : undefined;
    const email = col.email >= 0 ? cells[col.email] : undefined;
    rows.push({ phone, ...(name ? { name } : {}), ...(email ? { email } : {}) });
  }
  return rows;
}
